import { Alert, Grid, Typography } from '@mui/material';
import moment from 'moment';
import React from 'react';
import { Create, DateInput, FormDataConsumer, NumberInput, SimpleForm, TextInput, minValue, required, useGetOne } from 'react-admin';
import DateTimeInputUTC from '../../components/DateTimeInputUTC';
import { MAX_DATE } from '../../constants';
import { getChainId } from '../../lib';
import { validateDrawingNumbers } from './drawingNumbers';

/**
 * O tema do react-admin v5 seta `fullWidth: true` como defaultProps de
 * MuiTextField/MuiFormControl/MuiAutocomplete, então todo input estica pra 100%
 * do formulário. Cada input abaixo usa `fullWidth={false}` + uma destas larguras.
 */
export const FIELD_WIDTH = {
  name: 420,
  url: 420,
  date: 200,
  dateTime: 240,
  number: 190,
  rule: 260,
  fuel: 220,
  valueType: 170,
  value: 160,
  luckyNumberAmount: 180,
  stations: 520,
} as const;

export const getDrawingRuleName = (
  id: string, drawingRules: Array<{ id: string, name: string }>
) => {
  const drawingRule = drawingRules.find((dr) => dr.id === id);
  return drawingRule ? drawingRule.name : '';
};

export const getLabelValue = (
  id: string, drawingRules: Array<{ id: string, name: string }>
) => {
  const drawingRuleName = getDrawingRuleName(id, drawingRules);

  switch (drawingRuleName) {
    case 'fillin-amount':
      return 'Volume';
    case 'fillin-count':
      return 'Quantidade'
    case 'fillin-value':
      return 'Valor';
    case 'fillin':
      return 'Litros';
    case 'points-exchange':
      return 'Custo em pontos';
    default:
      return '';
  }
};

const DrawingCreate = (props) => {
  // Sorteio dirigido pelo Clube de Vantagens depende do módulo: com useGift = false
  // toda concessão fica atrás de um portão fechado e o sorteio não daria número nenhum.
  const chainId = getChainId();
  const { data: chain } = useGetOne('chains', { id: chainId }, { enabled: !!chainId });



  const validateDrawing = validateDrawingNumbers;

  // Sorteio novo nasce SEMPRE com as regras no Clube de Vantagens. Não é opção:
  // o motor legado (regras dentro do próprio sorteio) só continua vivo para os
  // sorteios que já existem. Forçado aqui no transform, e não num input escondido,
  // para não depender do estado do formulário.
  const transform = (data) => ({ ...data, isClubDriven: true });

  return (
    <Create
      {...props}
      title='Criar Sorteio'
      redirect="list"
      transform={transform}
    >
      <SimpleForm validate={validateDrawing}>
        <Grid container spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <TextInput
              label="Nome"
              source="name"
              fullWidth={false}
              sx={{ width: { xs: '100%', sm: FIELD_WIDTH.name } }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <DateInput
              fullWidth={false}
              sx={{ width: { xs: '100%', sm: FIELD_WIDTH.date } }}
              source="startDate"
              label="Data inicial"
              validate={[required()]}
              inputProps={{ max: MAX_DATE }}
              parse={value => moment(value).startOf('day').toISOString()}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <DateInput
              fullWidth={false}
              sx={{ width: { xs: '100%', sm: FIELD_WIDTH.date } }}
              source="endDate"
              label="Data final"
              validate={[required()]}
              inputProps={{ max: MAX_DATE }}
              parse={value => moment(value).endOf('day').toISOString()}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <DateTimeInputUTC
              fullWidth={false}
              sx={{ width: { xs: '100%', sm: FIELD_WIDTH.dateTime } }}
              source="drawingDate"
              label="Data e hora do sorteio"
              validate={[required()]}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <NumberInput
              fullWidth={false}
              sx={{ width: { xs: '100%', sm: FIELD_WIDTH.number } }}
              source="startNumber"
              label="Número inicial"
              min={0}
              validate={[required(), minValue(0)]}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <NumberInput
              fullWidth={false}
              sx={{ width: { xs: '100%', sm: FIELD_WIDTH.number } }}
              source="endNumber"
              label="Número final"
              min={1}
              validate={[required(), minValue(1)]}
            />
          </Grid>

          <FormDataConsumer >
            {({ formData }) => {
              if (formData.endNumber !== null && formData.startNumber !== null && formData.endNumber > formData.startNumber) {
                const totalNumbers = formData.endNumber - formData.startNumber + 1;
                const exceedsLimit = totalNumbers > 10_000_000;

                const endNumberPlaces = formData.endNumber.toString().split('');
                const startNumberPlaces = formData.startNumber.toString().split('');

                let startNumber = formData.startNumber.toString();
                if (endNumberPlaces.length > startNumberPlaces.length) {
                  startNumber = endNumberPlaces.slice(0, endNumberPlaces.length - startNumberPlaces.length).reduce((acc, cur) => acc += '0', '') + formData.startNumber;
                }
                return (
                  <Grid size={{ xs: 12, sm: 'grow' }} sx={{ minWidth: 280, alignSelf: 'center' }}>
                    <Typography variant="subtitle1" color="textSecondary">
                      Serão gerados {totalNumbers.toLocaleString('pt-BR')} números de {startNumber} até {formData.endNumber}, sempre com {endNumberPlaces.length} dígitos
                    </Typography>
                    {exceedsLimit && (
                      <Typography variant="subtitle2" color="error" style={{ marginTop: 2 }}>
                        O limite máximo é de 10.000.000 de números da sorte.
                      </Typography>
                    )}
                  </Grid>
                );
              }
              return <></>;

            }}
          </FormDataConsumer>
        </Grid>

        <Grid container spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <NumberInput
              fullWidth={false}
              sx={{ width: { xs: '100%', sm: FIELD_WIDTH.number } }}
              source="winningNumber"
              label="Número Sorteado"
              min={0}
              validate={[minValue(0)]}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <TextInput
              source="drawingRegulationUrl"
              label="Url do regulamento"
              fullWidth={false}
              sx={{ width: { xs: '100%', sm: FIELD_WIDTH.url } }}
            />
          </Grid>
        </Grid>

        {chain && !chain.useGift && (
          <Alert severity="warning" sx={{ width: '100%', marginTop: 3 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Esta rede está com o Clube de Vantagens desligado
            </Typography>
            <Typography variant="body2">
              Sem o Clube não há onde cadastrar a regra do número da sorte, e o sorteio não
              concederia número nenhum. Habilite o módulo na rede antes de criar o sorteio.
            </Typography>
          </Alert>
        )}

        <Alert severity="info" sx={{ width: '100%', marginTop: 3 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            As regras deste sorteio ficam no Clube de Vantagens
          </Typography>
          <Typography variant="body2">
            {'O sorteio não tem mais regras próprias. Para o cliente ganhar um número da sorte, crie um brinde no Clube de Vantagens com a recompensa '}
            <strong>Número da Sorte</strong>
            {' apontando para este sorteio, e configure ali quem ganha, quando e quantos números.'}
          </Typography>
          <Typography variant="body2" sx={{ marginTop: 1 }}>
            {'A vantagem é o que o Clube já sabe fazer e o sorteio nunca soube: regra por '}
            <strong>compra na loja</strong>
            {', limite por cliente, segmentação por etiqueta, agendamento por dia e horário, e mais de um brinde no mesmo sorteio.'}
          </Typography>
          <Typography variant="body2" sx={{ marginTop: 1 }}>
            <strong>Sorteios criados antes desta mudança</strong>
            {' continuam funcionando com as regras que já têm — nada precisa ser refeito.'}
          </Typography>
        </Alert>
        <Typography variant="subtitle1" color="textSecondary" style={{ marginTop: 16, width: '100%' }}>
          * Os sorteios devem ser registrados na Caixa Econômica Federal e o registro é de responsabilidade do cliente.
        </Typography>
      </SimpleForm>
    </Create>
  );
};

export default DrawingCreate;
